import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import type { SegmentErrorDetailOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import {
  ProductFormatTag,
  ProductVariantTag,
  SegmentErrorReasonTag,
} from '@/components/progress/tags';
import { SEGMENT_ERROR_SHOW_TABS } from '../constants';
import type { SegmentErrorShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<SegmentErrorShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface SegmentErrorShowHeaderProps {
  record: SegmentErrorDetailOut;
  activeTab: SegmentErrorShowTab;
  onTabChange: (key: string) => void;
}

export const SegmentErrorShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: SegmentErrorShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const hasProgression =
    record.total_start_progression != null &&
    record.total_end_progression != null;

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Rejected segments may carry no UUID — the id stands in for it. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            {record.segment_uuid ?? `#${record.id}`}
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t('progress.segment_errors.titles.show')} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('progress.segment_errors.fields.reason')}>
              {orDash(
                record.reason && <SegmentErrorReasonTag value={record.reason} />
              )}
            </StatCell>
            <StatCell label={t('progress.segment_errors.fields.product_kind')}>
              {orDash(
                record.product_kind && (
                  <ProductFormatTag
                    value={record.product_kind}
                    labels="segment_errors"
                  />
                )
              )}
            </StatCell>
            <StatCell
              label={t('progress.segment_errors.fields.product_variant')}
            >
              {orDash(
                record.product_variant && (
                  <ProductVariantTag
                    value={record.product_variant}
                    labels="segment_errors"
                  />
                )
              )}
            </StatCell>
            <StatCell label={t('progress.segment_errors.sections.progression')}>
              {orDash(
                hasProgression &&
                  `${record.total_start_progression!.toFixed(8)} → ${record.total_end_progression!.toFixed(8)}`
              )}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={SEGMENT_ERROR_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
